// src/render/lighting/mask.ts — full-screen RenderTexture light mask (VIS-02, VIS-05, VIS-06).
//
// Invariants:
//   - Phaser 4 ESM has NO default export (PATTERNS.md Pattern C). Namespace import only.
//   - The mask clears to cool-blue tint EVERY frame where sim state changed
//     (Pitfall 5: skip redraw on unchanged sim tick — see redraw() below).
//   - Depth 15 sits between DEPTH_WALL (10) and DEPTH_CHARACTER (20) per
//     CONTEXT.md D-09 / D-11 + GameScene depth constants. The player silhouette
//     pops ABOVE the mask — dark-side-of-wall characters are visibly lit by
//     ambient darkness, exactly the read we want for the family playtest.
//   - Sim→Phaser sign convention for torch rotation: -facingRad * 180/PI
//     (via simFacingToPhaserAngleDeg from ./torch-cone). LOCKED by Test 7 of
//     mask.test.ts. Do NOT flip the sign without re-running that test.
//   - Render reads sim state (WorldState.playerFov + .lightsState + MapData.lights)
//     and NEVER writes back (D-17 one-way contract).
//
// Source: Phase 2 RESEARCH.md §Pattern 2 + §Pitfall 5, UI-SPEC.md §Light Mask Palette,
// CONTEXT.md D-09..D-12.

// Phaser 4 ESM has NO default export (PATTERNS.md Pattern C).
import * as Phaser from 'phaser';

import { LIGHT_ATLAS_REGISTRY } from './glow-atlas';
import {
  TORCH_CONE_TEXTURE_KEY,
  PERMANENT_GLOW_TEXTURE_KEY,
  MOTION_GLOW_TEXTURE_KEY,
  GLOW_SPRITE_NATIVE_RADIUS_PX,
  TORCH_CONE_NATIVE_RADIUS_PX,
  simFacingToPhaserAngleDeg,
} from './torch-cone';

// UI-SPEC §Light Mask Palette locked values:

/** Cool-blue darkness base fill (RGB 21, 40, 56) — ~25% luminance over the scene
 *  when composited via MULTIPLY. D-11 + UI-SPEC. */
export const DARKNESS_FILL_HEX = 0x152838;

/** Depth slot between DEPTH_WALL (10) and DEPTH_CHARACTER (20) per CONTEXT.md D-09. */
export const DEPTH_LIGHT_MASK = 15;

/**
 * Tint applied to AI-hunter torch cones (D-11 folded render todo). The player's
 * own cone is rendered UNTINTED (warm placeholder PNG) so a hider/spectator
 * reads "that bright cone is THEIR light, not mine." Coral-leaning danger hue
 * from the 07-UI-SPEC §Color destructive accent. ADD-blended over the darkness
 * fill so it reads as a warm-red sweep on-device.
 */
export const AI_HUNTER_CONE_TINT = 0xff6b5c;

/**
 * One permanent or motion light at a world-pixel coord.
 * `alpha` is the final per-stamp alpha (for motion-light fade interpolation).
 * `radiusPx` drives the stamp `scale` relative to GLOW_SPRITE_NATIVE_RADIUS_PX.
 */
export interface LightInstance {
  x: number;
  y: number;
  alpha: number;
  radiusPx: number;
}

/**
 * Torch parameters for one cone. `facingRad` is the SIM facing (CCW-positive
 * y-UP); simFacingToPhaserAngleDeg converts to the Phaser stamp angle.
 *
 * P7 Plan 07-07 (D-11): a cone is stamped for the local player ('e0', when
 * torchOn AND WorldState.playerFov !== null) AND for every AI hunter with
 * torchOn. `isPlayer` selects the tint — the player cone is untinted (warm
 * placeholder), AI hunter cones use AI_HUNTER_CONE_TINT so a hider reads
 * "their light, not mine."
 *
 * Phase 08.1 Item 3: `colorOverride` supplies an explicit packed 24-bit colour
 * for static authored cone lights (not hunter torches). When present it replaces
 * both the player-untinted path and AI_HUNTER_CONE_TINT so the lamp visually
 * reads as the authored colour rather than the danger-hue combat convention.
 */
export interface TorchParams {
  x: number;
  y: number;
  facingRad: number;
  /** true = local player's cone (untinted); false = AI hunter (distinct tint). */
  isPlayer: boolean;
  /**
   * Phase 08.1 Item 3: authored color for static cone lights.
   * When present, overrides both player-untinted and AI_HUNTER_CONE_TINT so
   * the static lamp reads as its authored warm-amber (or custom) colour.
   * Absent for all dynamic torch cones (hunter / player).
   */
  colorOverride?: number;
}

export interface LightMaskRedrawParams {
  /** Base darkness fill colour each frame (use DARKNESS_FILL_HEX at the call site). */
  darkTint: number;
  permanentLights: readonly LightInstance[];
  motionLights: readonly LightInstance[];
  /**
   * P7 Plan 07-07 (D-11): one entry per torch cone to stamp this frame — the
   * player's cone plus every AI hunter with torchOn. Empty when no torch is lit.
   * Replaces the pre-P7 single `torch: TorchParams | null`.
   */
  torchCones: readonly TorchParams[];
  /** Sim tick — used for Pitfall 5 skip-if-unchanged guard. */
  tick: number;
}

/**
 * Full-screen Phaser RenderTexture composited over the scene with MULTIPLY blend.
 *
 * Contract:
 *   - Constructor sizes the RT to the full map in world pixels. setScrollFactor(1)
 *     so it follows the camera in world space (cheaper than a screen-sized RT
 *     with per-frame coordinate translation).
 *   - redraw() stamps ALL lights in a single pass. Skipped when sim tick is
 *     unchanged (Pitfall 5 — 3-line perf guardrail for 60 Hz render on mid-range
 *     Android).
 *   - The glow-atlas hot-swap registry (./glow-atlas.ts) is consulted first;
 *     the placeholder keys are the fallback (empty-string registry entry means
 *     "use placeholder"). This is the P6 drop-in path with zero render rewrite.
 */
export class LightMask {
  private rt: Phaser.GameObjects.RenderTexture;
  private lastRenderedTick = -1;

  constructor(scene: Phaser.Scene, mapWidthPx: number, mapHeightPx: number) {
    // Phaser 4: scene.add.renderTexture(x, y, width, height) — signature at
    // types/Phaser.d.ts ~46954.
    this.rt = scene.add.renderTexture(0, 0, mapWidthPx, mapHeightPx);
    this.rt.setOrigin(0, 0);
    this.rt.setDepth(DEPTH_LIGHT_MASK);
    this.rt.setBlendMode(Phaser.BlendModes.MULTIPLY);
    // Follows the camera at world rate — the mask sits in world space, not
    // screen space (RESEARCH Pattern 2 note on setScrollFactor).
    this.rt.setScrollFactor(1);
  }

  /**
   * Clear to darkness + stamp additive glow sprites.
   *
   * Pitfall 5 skip-if-unchanged-tick: at 60 Hz render over a 30 Hz sim, half the
   * frames have IDENTICAL sim state. Re-stamping produces the same pixels and
   * thrashes the Phaser command buffer on mid-range Android. Gate is keyed on
   * `params.tick` alone — the sim tick monotonically increases by design, so a
   * duplicate tick means none of (playerFov, lightsState, entity positions,
   * torchOn) could have changed, which means the mask output is byte-identical
   * to the previous call. Torch motion-light alpha interpolation happens in the
   * caller (GameScene.drawLightMask) using fadingTick + current tick so that
   * computation is part of the tick-gated payload.
   */
  redraw(params: LightMaskRedrawParams): void {
    if (params.tick === this.lastRenderedTick) return;
    this.lastRenderedTick = params.tick;

    // 1. Cool-blue darkness fill — full alpha; MULTIPLY over scene is what
    //    produces the ~25% luminance dimming (UI-SPEC).
    this.rt.clear();
    this.rt.fill(params.darkTint, 1.0);

    // Resolve texture keys via the hot-swap registry (empty string → fallback
    // to the placeholder preloaded by BootScene). P6 swaps real art in by
    // calling registerLightAtlas() with no render-layer change required.
    const permanentKey =
      LIGHT_ATLAS_REGISTRY.permanent !== ''
        ? LIGHT_ATLAS_REGISTRY.permanent
        : PERMANENT_GLOW_TEXTURE_KEY;
    const motionKey =
      LIGHT_ATLAS_REGISTRY.motion !== ''
        ? LIGHT_ATLAS_REGISTRY.motion
        : MOTION_GLOW_TEXTURE_KEY;
    const torchKey =
      LIGHT_ATLAS_REGISTRY.torch !== ''
        ? LIGHT_ATLAS_REGISTRY.torch
        : TORCH_CONE_TEXTURE_KEY;

    // 2. Permanent glow sprites (ADD blend, scale to authored tile-radius).
    for (const L of params.permanentLights) {
      this.rt.stamp(permanentKey, 0, L.x, L.y, {
        alpha: L.alpha,
        scale: L.radiusPx / GLOW_SPRITE_NATIVE_RADIUS_PX,
        blendMode: Phaser.BlendModes.ADD,
      });
    }

    // 3. Motion glow sprites (same contract; alpha is already fade-interpolated
    //    by the caller using fadingTick + current tick + P2_MOTION_LIGHT_FADE_TICKS).
    for (const L of params.motionLights) {
      this.rt.stamp(motionKey, 0, L.x, L.y, {
        alpha: L.alpha,
        scale: L.radiusPx / GLOW_SPRITE_NATIVE_RADIUS_PX,
        blendMode: Phaser.BlendModes.ADD,
      });
    }

    // 4. Torch cones (ADD blend, rotated to -facing via sign-convention helper).
    //    P7 Plan 07-07 (D-11): one stamp per cone — the player's plus every AI
    //    hunter with torchOn. The player cone is untinted (byte-identical to the
    //    pre-P7 single-cone path); AI hunter cones carry AI_HUNTER_CONE_TINT so
    //    a hider reads "their light, not mine." Mirrors the permanent/motion
    //    light loops above. ~8 hunters max — batched into one command buffer.
    //
    //    Phase 08.1 Item 3: static authored cone lights also land here. They carry
    //    `colorOverride` instead of using the isPlayer/AI_HUNTER_CONE_TINT branch
    //    so the lamp visually reads as the authored warm-amber (or custom) colour.
    for (const cone of params.torchCones) {
      // Tint resolution (priority: colorOverride > isPlayer-untinted > AI_HUNTER_CONE_TINT):
      //   - colorOverride set → use it (authored static lamp; no combat colour semantics)
      //   - isPlayer true    → no tint (player's own warm cone; pre-P7 invariant)
      //   - otherwise        → AI_HUNTER_CONE_TINT (danger hue for dynamic hunter cones)
      const tintConfig: { tint?: number } = {};
      if (cone.colorOverride !== undefined) {
        tintConfig.tint = cone.colorOverride;
      } else if (!cone.isPlayer) {
        tintConfig.tint = AI_HUNTER_CONE_TINT;
      }
      this.rt.stamp(torchKey, 0, cone.x, cone.y, {
        alpha: 1,
        angle: simFacingToPhaserAngleDeg(cone.facingRad),
        blendMode: Phaser.BlendModes.ADD,
        ...tintConfig,
      });
    }

    // RenderTexture is command-buffered in Phaser 4 — render() flushes the
    // accumulated stamps to the GPU.
    this.rt.render();
  }

  /** Release the RenderTexture GPU resources. Called by GameScene.shutdown. */
  destroy(): void {
    this.rt.destroy();
  }

  /** Native radius of the torch-cone placeholder PNG — exposed so the caller
   *  can compute per-frame scale if torch reach changes dynamically in P7. */
  static readonly TORCH_CONE_NATIVE_RADIUS_PX = TORCH_CONE_NATIVE_RADIUS_PX;
}
